import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Transition } from "@headlessui/react";
import { useCallback, useEffect, useMemo, useRef, } from "react";
import { classes } from "../../../../utils/classes";
import { sortPartialDates } from "../../../../utils/dateManager";
import { typeSafeObjectEntries } from "../../../../utils/helpers";
import { sortAmbassadors } from "../../../../utils/sorting";
import { useAmbassadors } from "../../../../hooks/useAmbassadors";
import useSettings from "../../hooks/useSettings";
import AmbassadorButton from "../../../../components/AmbassadorButton";
import AmbassadorCard from "../../../../components/AmbassadorCard";
import IconChevron from "../../../../components/icons/IconChevron";
var arrowClass = "absolute border-0 cursor-pointer text-alveus-green w-full h-[var(--list-fade-padding)] z-20 transition-opacity group pt-[var(--twitch-vertical-padding)] pb-4 box-content";
var arrowSvgClass = "mx-auto drop-shadow-lg overflow-visible transition-transform group-hover:scale-125 group-focus:scale-125";
var arrowPathClass = "[&_path]:stroke-alveus-tan [&_path]:stroke-[0.25rem] [&_path]:[paint-order:stroke] [&_path]:transition-[stroke] [&_path]:group-hover:stroke-highlight [&_path]:group-hover:stroke-[0.375rem] [&_path]:group-focus:stroke-highlight [&_path]:group-focus:stroke-[0.375rem]";
var hiddenClass = "opacity-0 pointer-events-none";
export default function Ambassadors(props) {
    var settings = useSettings();
    var _a = props.context, activeAmbassador = _a.activeAmbassador, setActiveAmbassador = _a.setActiveAmbassador, className = props.className, _b = props.plants, plants = _b === void 0 ? false : _b;
    var rawAmbassadors = useAmbassadors();
    var ambassadors = useMemo(function () {
        return sortAmbassadors(typeSafeObjectEntries(rawAmbassadors !== null && rawAmbassadors !== void 0 ? rawAmbassadors : {}).filter(function (_a) {
            var ambassador = _a[1];
            return (ambassador.species.class.key === "plantae") === plants;
        }), settings.ambassadorSort.value, sortPartialDates);
    }, [rawAmbassadors, plants, settings.ambassadorSort.value]);
    var upArrowRef = useRef(null);
    var ambassadorList = useRef(null);
    var downArrowRef = useRef(null);
    // Scroll the ambassador list to the selected ambassador
    useEffect(function () {
        if (!ambassadorList.current ||
            !activeAmbassador.key ||
            !activeAmbassador.isCommand)
            return;
        var offset = 200;
        var anchorElement = ambassadorList.current.querySelector("#".concat(activeAmbassador.key));
        if (anchorElement instanceof HTMLButtonElement)
            ambassadorList.current.scrollTo({
                top: Math.max(0, anchorElement.offsetTop - offset),
                behavior: "smooth",
            });
    }, [activeAmbassador]);
    // Allow the list to be scrolled via the buttons
    var ambassadorListScroll = useCallback(function (event, direction) {
        if (ambassadorList.current) {
            event.stopPropagation();
            ambassadorList.current.scroll({
                top: ambassadorList.current.scrollTop - direction,
                left: 0,
                behavior: "smooth",
            });
        }
    }, []);
    // Ensure the buttons are only shown if the list is scrollable
    var handleArrowVisibility = useCallback(function () {
        var _a, _b, _c, _d;
        var list = ambassadorList.current;
        if (!list)
            return;
        var listRect = list.getBoundingClientRect();
        var firstRect = (_a = list.firstElementChild) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        var lastRect = (_b = list.lastElementChild) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
        if (!firstRect || !lastRect)
            return;
        // If more than 50% of the first element is hidden, show the up arrow
        for (var _i = 0, _e = hiddenClass.split(" "); _i < _e.length; _i++) {
            var className_1 = _e[_i];
            (_c = upArrowRef.current) === null || _c === void 0 ? void 0 : _c.classList.toggle(className_1, firstRect.top >= listRect.top + firstRect.height / 2);
        }
        // If more than 50% of the last element is hidden, show the down arrow
        for (var _f = 0, _g = hiddenClass.split(" "); _f < _g.length; _f++) {
            var className_2 = _g[_f];
            (_d = downArrowRef.current) === null || _d === void 0 ? void 0 : _d.classList.toggle(className_2, lastRect.bottom <= listRect.bottom - lastRect.height / 2);
        }
    }, []);
    // Check the arrow visibility on mount, as browsers restore odd scroll positions
    // Also, check it whenever the ambassador list changes as the list may change size
    useEffect(function () {
        handleArrowVisibility();
        // If the window is resized, check the arrow visibility again
        window.addEventListener("resize", handleArrowVisibility);
        return function () { return window.removeEventListener("resize", handleArrowVisibility); };
    }, [handleArrowVisibility, ambassadors]);
    return (_jsxs("div", { className: classes("absolute top-0 left-0 z-0 grid h-full grid-cols-auto-2 grid-rows-1", className), children: [_jsxs("div", { className: "relative z-10 flex flex-col items-center", children: [_jsx("div", { ref: ambassadorList, className: "list-fade -my-[var(--twitch-vertical-padding)] scrollbar-none flex w-40 flex-col items-center gap-4 overflow-scroll px-4 py-[calc(var(--twitch-vertical-padding)+var(--list-fade-padding))]", onScroll: handleArrowVisibility, children: ambassadors.map(function (_a) {
                            var key = _a[0];
                            return (_jsx(AmbassadorButton, { ambassador: key, onClick: function () {
                                    setActiveAmbassador(function (prev) {
                                        return prev.key === key ? {} : { key: key };
                                    });
                                }, className: "w-full", active: activeAmbassador.key === key }, key));
                        }) }), _jsx("button", { ref: upArrowRef, className: classes(arrowClass, "-top-[var(--twitch-vertical-padding)]", hiddenClass), onClick: function (e) { return ambassadorListScroll(e, 250); }, title: "Scroll up", type: "button", "data-transparent-clicks": true, children: _jsx(IconChevron, { className: classes(arrowSvgClass, arrowPathClass) }) }), _jsx("button", { ref: downArrowRef, className: classes(arrowClass, "-bottom-[var(--twitch-vertical-padding)] rotate-180"), onClick: function (e) { return ambassadorListScroll(e, -250); }, title: "Scroll down", type: "button", "data-transparent-clicks": true, children: _jsx(IconChevron, { className: classes(arrowSvgClass, arrowPathClass) }) })] }), ambassadors.map(function (_a) {
                var key = _a[0];
                return (_jsx(Transition, { show: activeAmbassador.key === key, children: _jsx("div", { className: "z-0 col-start-2 row-start-1 flex max-h-full origin-[center_left] self-center transition-[opacity,transform,translate] will-change-[opacity,transform,translate] data-[closed]:-translate-x-10 data-[closed]:opacity-0 data-[closed]:motion-reduce:translate-x-0", children: _jsx(AmbassadorCard, { ambassador: key, onClose: function () { return setActiveAmbassador({}); }, disableCardEffects: settings.disableCardEffects.value }, key) }) }, key));
            })] }));
}
